
import {
  Component, Prop, Ref, Watch, Mixins, Inject,
} from 'vue-property-decorator';
import { InputValidationRules } from 'vuetify';
import AddressBook from '@/features/shipment/components/createShipment/AddressBook.vue';
import AddressAutocompleteLookup from '@/features/shipment/components/createShipment/AddressAutocompleteLookup.vue';
import SwapButtonNew from '@/features/shipment/components/createShipment/SwapButtonNew.vue';
import AddressToggleChip from '@/features/shipment/components/createShipment/AddressToggleChip.vue';
import AddressBookIconButton from '@/features/shipment/components/createShipment/AddressBookIconButton.vue';
import ShipToDropdown from '@/features/shipment/components/createShipment/ShipToDropdown.vue';
import SoldToPanel from '@/features/shipment/components/createShipment/SoldToPanel.vue';
import ExpandableInput from '@/features/common/components/ExpandableInput.vue';
import SaveToBook from '@/features/shipment/components/SaveToBook.vue';
import MobileCollapsedAddress from '@/features/shipment/components/createShipment/MobileCollapsedAddress.vue';
import { CityProvinceLookupMixin } from '@/features/shipment/components/mixins';
import { CountryInfo, VForm } from '@/features/common/types';
import {
  ShipFrom, ShipTo, InProgress, SoldToState,
} from '@/features/shipment/types';
import eventBus from '@/features/common/eventBus';
import { UserPreferences } from '@/features/settings/types';
import { createAddressBookItem } from '@/features/shipment/services/addresses-book.services';
import { patchPreferences } from '@/features/settings/services/preferences.services';
import { showErrorAlertDialog } from '@/features/common/helpers';
import {
  requiredRules,
  phoneNumberInvalidRules,
  stripUnoCharFromPostalCode,
  emailRules,
  minCharactersRules,
  contactOrCompanyRule,
  isProvinceRequired,
  getPostalCodeRules,
} from '@/features/common/helpers/validationRules';
import { safeListedCountryCodes } from '@/features/common/raw';
import eventNames from '@/features/common/constants/events';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import UndoClearMixin from '@/features/common/mixins/undoClearMixin';
import FeatureFlagMixin from '@/features/common/mixins/featureFlagMixin';
import {
  rulesIfAttempted as rulesIfAttemptedFn,
  CreateShipmentValidationState,
} from '@/features/shipment/helpers/createShipmentValidation';
import { trackCreateFieldComplete } from '@/features/common/helpers/tracked-events';
import { compareAddresses, isSoldToDataValid } from '../../helpers/createShipment';

@Component({
  components: {
    AddressBook,
    AddressAutocompleteLookup,
    SwapButtonNew,
    AddressToggleChip,
    AddressBookIconButton,
    ShipToDropdown,
    SoldToPanel,
    ExpandableInput,
    MobileCollapsedAddress,
    SaveToBook,
  },
})
export default class WhereShipmentForm extends Mixins(
  CityProvinceLookupMixin, ResponsivenessMixin, UndoClearMixin, FeatureFlagMixin,
) {
  protected featureKeysToLoad = ['soldToFeature'];

  @Inject({
    from: 'createShipmentValidationState',
    default: () => ({ attempted: true }),
  })
  private createShipmentValidationState!: CreateShipmentValidationState;

  @Prop({ default: false }) showImport!: boolean;

  @Ref('formWhereShipment') formWhereShipment!: VForm;

  private fromData: ShipFrom = {
    company: '',
    contact: '',
    phone: '',
    addr1: '',
    addr2: '',
    addr3: '',
    countryCode: '',
    postalCode: '',
    city: '',
    province: '',
    residential: false,
    emails: [],
    isInside: false,
    isTailGate: false,
    isTradeShow: false,
    isLimitedAccess: false,
  }

  private toData: ShipTo = {
    company: '',
    contact: '',
    phone: '',
    addr1: '',
    addr2: '',
    addr3: '',
    countryCode: '',
    postalCode: '',
    province: '',
    city: '',
    residential: false,
    emails: [],
    isInside: false,
    isTailGate: false,
    isTradeShow: false,
    isLimitedAccess: false,
  }

  private showAddressBook = false;

  private saveAddressFrom = false;

  private saveAddressTo = false;

  private shipFromMobileCollapsed = false;

  private shipFromKeepOpen = false;

  private addressType: 'fromAddress' | 'toAddress' | 'soldToAddress' = 'fromAddress';

  private activePanel: 'shipTo' | 'soldTo' = 'shipTo';

  private soldToState: SoldToState = {
    entityMatch: 'same',
    address: {
      contact: '',
      company: '',
      phone: '',
      addr1: '',
      addr2: '',
      addr3: '',
      countryCode: '',
      postalCode: '',
      province: '',
      city: '',
      emails: [],
    },
  };

  private requiredRules = requiredRules;

  private contactOrCompanyRule = contactOrCompanyRule;

  private minCharactersRules = minCharactersRules;

  private emailRules = emailRules;

  private phoneNumberInvalidRules = phoneNumberInvalidRules;

  private defaultAddressInput = {
    isChecked: false,
    show: false,
    loading: false,
  };

  private createAddressBookItem = createAddressBookItem;

  private isFormChanged = false;

  protected onUndoRestored(): void {
    // eslint-disable-next-line no-unused-expressions
    this.formWhereShipment?.resetValidation();
  }

  private fromPostalBlurred = false;

  private toPostalBlurred = false;

  private showSaveSuccess = false;

  private saveSuccessTimer: ReturnType<typeof setTimeout> | null = null;

  // -- Address text computed (maps textarea to addr1/addr2/addr3) --

  get fromAddressText(): string {
    return [this.fromData.addr1, this.fromData.addr2, this.fromData.addr3]
      .filter(Boolean).join('');
  }

  set fromAddressText(val: string) {
    const text = val || '';
    this.fromData.addr1 = text.substring(0, 35);
    this.fromData.addr2 = text.substring(35, 70);
    this.fromData.addr3 = text.substring(70, 105);
  }

  get toAddressText(): string {
    return [this.toData.addr1, this.toData.addr2, this.toData.addr3]
      .filter(Boolean).join('');
  }

  set toAddressText(val: string) {
    const text = val || '';
    this.toData.addr1 = text.substring(0, 35);
    this.toData.addr2 = text.substring(35, 70);
    this.toData.addr3 = text.substring(70, 105);
  }

  // -- Email computed --

  get fromAddressEmail(): string {
    return this.fromData.emails ? this.fromData.emails[0] : '';
  }

  set fromAddressEmail(val: string) {
    this.fromData = {
      ...this.fromData,
      emails: val ? [val] : [],
    };
  }

  get toAddressEmail(): string {
    return this.toData.emails ? this.toData.emails[0] : '';
  }

  set toAddressEmail(val: string) {
    this.toData = {
      ...this.toData,
      emails: val ? [val] : [],
    };
  }

  get isSoldToEnabled(): boolean {
    return !!this.featureFlags.soldToFeature;
  }

  get isToCountryUS(): boolean {
    return this.toData.countryCode === 'US';
  }

  get toEmailRules() {
    return this.isToCountryUS
      ? [...requiredRules, ...emailRules]
      : [...emailRules];
  }

  // -- Store getters --

  get inProgressData(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get isInProgressData(): number {
    return this.$store.getters.getIsInProgressData;
  }

  get isUserPreferences(): number {
    return this.$store.getters.getIsUserPreferences;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  // -- Country lookups --

  get fromCurrentCountry(): CountryInfo | undefined {
    return this.$store.getters.getCountryFromCode(this.fromData.countryCode);
  }

  get toCurrentCountry(): CountryInfo | undefined {
    return this.$store.getters.getCountryFromCode(this.toData.countryCode);
  }

  // -- Postal code rules --

  get fromPostalCodeRules(): InputValidationRules {
    if (!this.fromCurrentCountry) return this.requiredRules;
    return getPostalCodeRules(this.fromCurrentCountry);
  }

  get toPostalCodeRules(): InputValidationRules {
    if (!this.toCurrentCountry) return this.requiredRules;
    return getPostalCodeRules(this.toCurrentCountry);
  }

  // -- Province labels and rules --

  get fromProvinceLabel(): string {
    const label = this.fromData.countryCode === 'US'
      ? this.$t('labels.state') as string
      : this.$t('labels.province') as string;
    return this.fromProvinceRules.length ? `${label} *` : label;
  }

  get toProvinceLabel(): string {
    const label = this.toData.countryCode === 'US'
      ? this.$t('labels.state') as string
      : this.$t('labels.province') as string;
    return this.toProvinceRules.length ? `${label} *` : label;
  }

  get fromProvinceRules(): InputValidationRules {
    return isProvinceRequired(this.fromData.countryCode) ? this.requiredRules : [];
  }

  get toProvinceRules(): InputValidationRules {
    return isProvinceRequired(this.toData.countryCode) ? this.requiredRules : [];
  }

  // -- Conditional city/province visibility --

  get showFromCityProvince(): boolean {
    if (safeListedCountryCodes.includes(this.fromData.countryCode)) {
      // CA/US: show after postal blur or when city already filled
      return !!(this.fromData.countryCode
        && this.fromData.postalCode
        && (this.fromPostalBlurred || this.fromData.city));
    }
    return !!this.fromData.countryCode;
  }

  get showToCityProvince(): boolean {
    if (safeListedCountryCodes.includes(this.toData.countryCode)) {
      return !!(this.toData.countryCode
        && this.toData.postalCode
        && (this.toPostalBlurred || this.toData.city));
    }
    return !!this.toData.countryCode;
  }

  // -- Required fields validation --

  private isRequiredFieldsFilled = (address: ShipFrom | ShipTo): boolean => {
    const {
      contact, company, phone, addr1, countryCode, city, province,
    } = address;

    const hasContactOrCompany = !!(contact?.trim() || company?.trim());
    const isFilled = hasContactOrCompany
      && !!phone?.trim() && !!addr1?.trim() && !!countryCode?.trim() && !!city?.trim();

    return isProvinceRequired(countryCode) ? isFilled && !!province?.trim() : isFilled;
  }

  get isFromAddressRequiredFieldsFilled(): boolean {
    return this.isRequiredFieldsFilled(this.fromData);
  }

  get isToAddressRequiredFieldsFilled(): boolean {
    return this.isRequiredFieldsFilled(this.toData);
  }

  get isFromDataDefault(): boolean {
    const { defaultAddress } = this.userPreferences.shippingSettings;
    if (!defaultAddress) return false;
    return compareAddresses(this.fromData, defaultAddress);
  }

  // -- Watchers --

  @Watch('isUserPreferences', { immediate: true })
  private populateUserPrefsData(newValue: number): void {
    if (!newValue) return;

    const { defaultAddress } = this.userPreferences.shippingSettings;

    const {
      fromAddress,
    } = this.inProgressData;

    const userInfo = this.$store.getters.getUserInfo;
    this.fromData.emails = [userInfo.username];

    if (defaultAddress) {
      const { emails } = defaultAddress;
      this.fromData = {
        ...defaultAddress,
        emails: (emails && emails.length && emails[0]) ? emails : [userInfo.username],
      };
      if (defaultAddress.postalCode) this.fromPostalBlurred = true;

      if (Object.values(fromAddress).every(
        (x) => x === null || x === '' || x === undefined || x === false || x.length === 0,
      )) {
        this.$store.commit('patchInProgress', {
          key: 'fromAddress',
          value: { ...this.fromData },
        });
      }
    }

    this.$nextTick(() => this.resetFormValidation());
  }

  @Watch('isInProgressData', { immediate: true })
  private replaceShipmentData(newValue: number): void {
    if (!newValue) return;

    const {
      fromAddress, toAddress, soldTo, soldToAddress,
    } = this.inProgressData;

    if (toAddress) {
      this.toData = { ...toAddress };
      if (toAddress.postalCode) this.toPostalBlurred = true;
    }

    if (fromAddress) {
      this.fromData = { ...fromAddress };
      if (fromAddress.postalCode) this.fromPostalBlurred = true;
    }

    if (soldTo) {
      this.soldToState = {
        entityMatch: soldTo.entityMatch,
        address: { ...soldTo.address },
      };
    } else if (soldToAddress) {
      this.soldToState = {
        entityMatch: 'different',
        address: { ...soldToAddress },
      };
    }
  }

  @Watch('isMobile', { immediate: true })
  private syncShipFromMobileCollapsed(): void {
    if (!this.isMobile) return;
    this.shipFromMobileCollapsed = !this.shipFromKeepOpen;
  }

  @Watch('fromData.contact')
  private onFromContactChange(): void {
    this.$nextTick(() => this.revalidateInputById('from-company'));
  }

  @Watch('fromData.company')
  private onFromCompanyChange(): void {
    this.$nextTick(() => this.revalidateInputById('from-contact-name'));
  }

  @Watch('toData.contact')
  private onToContactChange(): void {
    this.$nextTick(() => this.revalidateInputById('to-company'));
  }

  @Watch('toData.company')
  private onToCompanyChange(): void {
    this.$nextTick(() => this.revalidateInputById('to-contact-name'));
  }

  private setShipFromMobileKeepOpen(keepOpen: boolean): void {
    this.shipFromKeepOpen = keepOpen;
    this.syncShipFromMobileCollapsed();
  }

  // -- Lifecycle --

  mounted(): void {
    this.$store.commit('setWhereShipmentForm', this.$refs.formWhereShipment);
    eventBus.$on(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.clearAllInformation);
    eventBus.$on(eventNames.SUBMIT_WHERE_SHIPMENT, this.submitWhereShipmentForm);

    // Clear validation errors that fire on mount (before user interacts)
    this.$nextTick(() => this.resetFormValidation());
  }

  destroyed(): void {
    eventBus.$off(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.clearAllInformation);
    eventBus.$off(eventNames.SUBMIT_WHERE_SHIPMENT);
    if (this.saveSuccessTimer) clearTimeout(this.saveSuccessTimer);
  }

  // -- Form validation --

  private rulesIfAttempted(rules?: InputValidationRules): InputValidationRules {
    return rulesIfAttemptedFn(rules, this.createShipmentValidationState.attempted);
  }

  private resetFormValidation(): void {
    this.createShipmentValidationState.attempted = false;
    if (this.$refs.formWhereShipment) {
      (this.$refs.formWhereShipment as HTMLFormElement)
        .resetValidation();
    }
  }

  private validateForm(): void {
    if (this.$refs.formWhereShipment) {
      (this.$refs.formWhereShipment as HTMLFormElement).validate();
    }
  }

  private revalidateInputById(elementId: string): void {
    if (!this.createShipmentValidationState.attempted) return;
    const form = this.$refs.formWhereShipment as any;
    if (!form?.inputs) return;
    const input = form.inputs.find(
      (i: any) => i.id === elementId || i.$attrs?.id === elementId
        || i.$el?.id === elementId,
    );
    if (input) input.validate(true);
  }

  private validateSectionInputs(sectionRef: string): void {
    const section = this.$refs[sectionRef] as any;
    const form = this.$refs.formWhereShipment as any;
    if (!section || !form?.inputs) return;
    const sectionEl = section.$el || section;
    form.inputs.forEach((input: any) => {
      if (sectionEl.contains(input.$el)) {
        input.validate(true);
      }
    });
  }

  private async validateFromFields(): Promise<boolean> {
    this.createShipmentValidationState.attempted = true;
    await this.$nextTick();
    this.validateSectionInputs('shipFromSection');
    return this.isFromAddressRequiredFieldsFilled;
  }

  private async validateToFields(): Promise<boolean> {
    this.createShipmentValidationState.attempted = true;
    await this.$nextTick();
    this.validateSectionInputs('shipToSection');
    return this.isToAddressRequiredFieldsFilled;
  }

  private clearAllInformation() {
    this.toData = {
      company: '',
      contact: '',
      phone: '',
      addr1: '',
      addr2: '',
      addr3: '',
      countryCode: '',
      postalCode: '',
      province: '',
      city: '',
      emails: [],
      residential: false,
      isInside: false,
      isTailGate: false,
      isTradeShow: false,
      isLimitedAccess: false,
    };

    this.activePanel = 'shipTo';
    this.soldToState = {
      entityMatch: 'same',
      address: {
        contact: '',
        company: '',
        phone: '',
        addr1: '',
        addr2: '',
        addr3: '',
        countryCode: '',
        postalCode: '',
        province: '',
        city: '',
        emails: [],
      },
    };

    this.isFormChanged = false;
    this.fromPostalBlurred = false;
    this.toPostalBlurred = false;

    this.$nextTick(() => {
      this.createShipmentValidationState.attempted = false;
      if (this.$refs.formWhereShipment) {
        (this.$refs.formWhereShipment as HTMLFormElement).resetValidation();
      }
    });
  }

  // -- Event handlers --

  private openImport = () => eventBus.$emit(eventNames.OPEN_SHIPMENT_ORDER_IMPORT);

  private handleSoldToUpdate(state: SoldToState): void {
    this.soldToState = state;
    this.updateInProgressData('soldTo', this.soldToState, true);
  }

  private showAddressBookDialog(addressType: 'fromAddress' | 'toAddress' | 'soldToAddress') {
    this.addressType = addressType;
    this.showAddressBook = true;
  }

  private selectAddressBook(address: ShipFrom | ShipTo) {
    const validatedAddress: ShipFrom | ShipTo = {
      ...address,
      postalCode: stripUnoCharFromPostalCode(address.postalCode),
    };

    if (this.addressType === 'fromAddress') {
      this.fromData = validatedAddress;
      if (validatedAddress.postalCode) this.fromPostalBlurred = true;
    } else if (this.addressType === 'toAddress') {
      this.toData = validatedAddress;
      if (validatedAddress.postalCode) this.toPostalBlurred = true;
    } else if (this.addressType === 'soldToAddress') {
      this.soldToState = {
        entityMatch: 'different',
        address: {
          contact: validatedAddress.contact,
          company: validatedAddress.company,
          phone: validatedAddress.phone,
          addr1: validatedAddress.addr1,
          addr2: validatedAddress.addr2,
          addr3: validatedAddress.addr3,
          countryCode: this.toData.countryCode,
          postalCode: validatedAddress.postalCode,
          province: validatedAddress.province,
          city: validatedAddress.city,
          emails: validatedAddress.emails,
        },
      };
      this.updateInProgressData('soldTo', this.soldToState, true);
      this.resetFormValidation();
      this.showAddressBook = false;
      return;
    }

    this.updateInProgressData(this.addressType, validatedAddress, true);
    this.resetFormValidation();

    this.showAddressBook = false;
  }

  private async addToAddressBook(response: unknown): Promise<void> {
    this.$store.commit('addAddressBookItem', response);

    if (this.saveSuccessTimer) clearTimeout(this.saveSuccessTimer);
    this.showSaveSuccess = true;
    this.$nextTick(() => {
      const ref = this.$refs.container;
      const el = ref && (ref as any).$el ? (ref as any).$el : ref;
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    this.saveSuccessTimer = setTimeout(() => {
      this.showSaveSuccess = false;
    }, 5000);
  }

  private async submitWhereShipmentForm(): Promise<void> {
    this.createShipmentValidationState.attempted = true;

    // If Sold to has "different" selected but fields are incomplete,
    // switch to the Sold to panel so validation errors render
    if (this.isSoldToEnabled
      && !isSoldToDataValid(this.soldToState)
      && this.activePanel !== 'soldTo') {
      this.activePanel = 'soldTo';
    }

    await this.$nextTick();
    this.validateForm();
  }

  private handleFromCountryChange(): void {
    this.isFormChanged = true;
    this.fromData.postalCode = '';
    this.fromData.city = '';
    this.fromData.province = '';
    this.updateInProgressData('fromAddress', this.fromData, true);
  }

  private handleToCountryChange(): void {
    this.isFormChanged = true;
    this.toData.postalCode = '';
    this.toData.city = '';
    this.toData.province = '';
    this.updateInProgressData('toAddress', this.toData, true);
  }

  // -- Postal code blur handlers --

  private handleFromPostalCodeBlur(): void {
    this.fromData.postalCode = stripUnoCharFromPostalCode(this.fromData.postalCode);
    if (this.fromData.postalCode) {
      this.fromData.postalCode = this.fromData.postalCode.toUpperCase();
      trackCreateFieldComplete(this.$gtag, { field: 'ship_from' });
    }
    this.fromPostalBlurred = true;
    this.updateInProgressData('fromAddress', this.fromData, true);
    this.handleAutofillCityProvince('fromData');
  }

  private handleToPostalCodeBlur(): void {
    this.toData.postalCode = stripUnoCharFromPostalCode(this.toData.postalCode);
    if (this.toData.postalCode) {
      this.toData.postalCode = this.toData.postalCode.toUpperCase();
      trackCreateFieldComplete(this.$gtag, { field: 'ship_to' });
    }
    this.toPostalBlurred = true;
    this.updateInProgressData('toAddress', this.toData, true);
    this.handleAutofillCityProvince('toData');
  }

  // -- Toggle chip handlers --

  private handleFromResidentialToggle(val: boolean): void {
    this.fromData.residential = val;
    this.updateInProgressData('fromAddress', this.fromData, true);
  }

  private handleFromBusinessToggle(val: boolean): void {
    this.fromData.residential = !val;
    this.updateInProgressData('fromAddress', this.fromData, true);
  }

  private handleToResidentialToggle(val: boolean): void {
    this.toData.residential = val;
    this.updateInProgressData('toAddress', this.toData, true);
  }

  private handleToBusinessToggle(val: boolean): void {
    this.toData.residential = !val;
    this.updateInProgressData('toAddress', this.toData, true);
  }

  private handleSetDefaultAddressChipToggle(val: boolean): void {
    this.defaultAddressInput.isChecked = val;
    if (val) {
      this.setAsDefaultAddress();
    }
  }

  // -- Default address --

  private async setAsDefaultAddress() {
    this.defaultAddressInput.loading = true;

    const response = await patchPreferences(this.userPreferences.id, [
      {
        path: 'shippingSettings/defaultAddress',
        op: 'replace',
        value: this.fromData,
      },
    ]);

    if (response) {
      this.defaultAddressInput.show = false;
      this.defaultAddressInput.isChecked = false;
      this.$store.commit('patchUserPreferences', {
        key: 'shippingSettings/defaultAddress',
        value: this.fromData,
      });
      this.updateInProgressData('fromAddress', this.fromData, true);
    } else {
      showErrorAlertDialog();
    }
    this.defaultAddressInput.loading = false;
  }

  // -- In-progress data --

  private updateInProgressData(key: string, value: unknown, force = false) {
    if (!force && !this.isFormChanged) return;

    this.$store.dispatch('saveInProgressData', {
      key,
      value,
    });
  }

  private handleAutofillCityProvince(data: string): void {
    const fill = (city: string, province: string, residential: boolean): void => {
      this[data].city = city || this[data].city;
      this[data].province = province || this[data].province;
      this[data].residential = residential || this[data].residential;

      if (data === 'fromData') {
        this.updateInProgressData('fromAddress', { ...this.fromData }, true);
      } else {
        this.updateInProgressData('toAddress', { ...this.toData }, true);
      }
    };

    this.autofillCityProvince(fill, data);
  }

  private handleSwap(): void {
    (this.$refs.formWhereShipment as HTMLFormElement).resetValidation();

    const tempFrom = this.fromData;
    this.fromData = this.toData;
    this.toData = tempFrom;

    // Swap blur flags too
    const tempBlur = this.fromPostalBlurred;
    this.fromPostalBlurred = this.toPostalBlurred;
    this.toPostalBlurred = tempBlur;

    this.$store.dispatch('swapInProgressAddresses', {
      fromAddress: this.fromData,
      toAddress: this.toData,
    });

    this.shipFromKeepOpen = false;
    this.$nextTick(() => this.syncShipFromMobileCollapsed());
  }
}
